import { useState, useEffect, useCallback } from 'react';
import { useSimulatorStore } from '../../stores/useSimulatorStore';
import { usePortfolioPnL } from '../../hooks/useCalculations';
import { formatCurrencyCompact, formatNumber, pnlColorClass } from '../../lib/formatters';
import { apiFetch } from '../../lib/api';
import { Save, Trash2, RotateCcw, ChevronDown, ChevronUp, Layers } from 'lucide-react';
import { InfoButton } from '../common/Tooltip';
import type { SavedScenario } from '../../types';

export function ScenarioManager() {
  const [expanded, setExpanded] = useState(false);
  const [scenarioName, setScenarioName] = useState('');
  const [saving, setSaving] = useState(false);
  const savedScenarios = useSimulatorStore(s => s.savedScenarios);
  const setSavedScenarios = useSimulatorStore(s => s.setSavedScenarios);
  const loadScenario = useSimulatorStore(s => s.loadScenario);
  const packages = useSimulatorStore(s => s.packages);
  const assumptions = useSimulatorStore(s => s.assumptions);
  const portfolio = usePortfolioPnL();

  const fetchScenarios = useCallback(async () => {
    try {
      const res = await apiFetch('/api/scenarios');
      if (res.ok) {
        const data: SavedScenario[] = await res.json();
        setSavedScenarios(data);
      }
    } catch { /* DB not available */ }
  }, [setSavedScenarios]);

  useEffect(() => { fetchScenarios(); }, [fetchScenarios]);

  const handleSave = async () => {
    const name = scenarioName.trim() || `Scenario ${savedScenarios.length + 1}`;
    setSaving(true);
    try {
      const res = await apiFetch('/api/scenarios', {
        method: 'POST',
        body: JSON.stringify({ name, assumptions, packages }),
      });
      if (res.ok) {
        const scenario: SavedScenario = await res.json();
        setSavedScenarios([scenario, ...savedScenarios]);
        setScenarioName('');
      }
    } catch { /* ignore */ }
    setSaving(false);
  };

  const handleDelete = async (id: string) => {
    try {
      await apiFetch(`/api/scenarios/${id}`, { method: 'DELETE' });
      setSavedScenarios(savedScenarios.filter(s => s.id !== id));
    } catch { /* ignore */ }
  };

  return (
    <div className="card card-accent-purple mb-4">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center justify-between w-full text-left"
      >
        <div className="flex items-center gap-2">
          <Layers size={14} className="text-chart-purple" />
          <h3 className="text-heading font-semibold text-secondary uppercase tracking-wider">Scenarios</h3>
          <InfoButton helpKey="scenarioManager" size={12} />
          {savedScenarios.length > 0 && (
            <span className="text-micro px-1.5 py-0.5 rounded-full bg-chart-purple/15 text-chart-purple font-medium">
              {savedScenarios.length} saved
            </span>
          )}
        </div>
        {expanded ? <ChevronUp size={14} className="text-muted" /> : <ChevronDown size={14} className="text-muted" />}
      </button>

      {expanded && (
        <div className="mt-3 space-y-3">
          <div className="flex gap-2">
            <input
              type="text"
              value={scenarioName}
              onChange={e => setScenarioName(e.target.value)}
              placeholder={`Scenario ${savedScenarios.length + 1}`}
              className="flex-1 px-2 py-1.5 text-xs rounded border border-border bg-transparent text-primary"
              onKeyDown={e => e.key === 'Enter' && handleSave()}
            />
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded bg-chart-purple/20 text-chart-purple hover:bg-chart-purple/30 border border-chart-purple/40 disabled:opacity-50"
            >
              <Save size={12} />
              {saving ? 'Saving...' : 'Save Current'}
            </button>
          </div>

          <div className="px-3 py-2 rounded-lg text-micro" style={{ backgroundColor: 'var(--color-surface)', border: '1px solid var(--color-border)' }}>
            <span className="text-muted">Current: </span>
            <span className="text-primary font-medium">{packages.length} packages</span>
            <span className="text-muted"> · P&L: </span>
            <span className={`font-mono font-semibold ${pnlColorClass(portfolio.portfolioPnLSplit)}`}>
              {formatCurrencyCompact(portfolio.portfolioPnLSplit)}
            </span>
            <span className="text-muted"> · Units: </span>
            <span className="font-mono text-primary">{formatNumber(Math.round(portfolio.totalS2UnitsSplit))}</span>
          </div>

          {savedScenarios.length > 0 && (
            <div className="space-y-2">
              <div className="text-xs text-muted uppercase tracking-wider font-semibold">Saved Scenarios</div>
              {savedScenarios.map(scenario => (
                <div key={scenario.id} className="flex items-center gap-2 px-3 py-2 rounded-lg border border-border/50 transition-colors hover:border-chart-purple/30" style={{ backgroundColor: 'var(--color-surface)' }}>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-medium text-primary truncate">{scenario.name}</div>
                    <div className="text-xs text-muted">
                      {scenario.packages.length} packages · {new Date(scenario.timestamp).toLocaleDateString()}
                    </div>
                  </div>
                  <button
                    onClick={() => loadScenario(scenario.id)}
                    className="flex items-center gap-1 px-2 py-1 text-micro font-medium rounded bg-chart-purple/10 text-chart-purple hover:bg-chart-purple/20"
                  >
                    <RotateCcw size={10} />
                    Load
                  </button>
                  <button
                    onClick={() => handleDelete(scenario.id)}
                    className="p-1 text-muted hover:text-metric-negative transition-colors"
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              ))}
            </div>
          )}

          {savedScenarios.length === 0 && (
            <p className="text-xs text-muted italic">No saved scenarios yet. Save your current configuration to compare later.</p>
          )}
        </div>
      )}
    </div>
  );
}
